import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Clock, Zap, Drum, Music } from 'lucide-react';

const sectionColors = {
  'Intro': { bg: '#3b82f6', hover: '#2563eb', light: 'rgba(59, 130, 246, 0.2)' },
  'Verse': { bg: '#22c55e', hover: '#16a34a', light: 'rgba(34, 197, 94, 0.2)' },
  'Pre-Chorus': { bg: '#eab308', hover: '#ca8a04', light: 'rgba(234, 179, 8, 0.2)' },
  'Chorus': { bg: '#ef4444', hover: '#dc2626', light: 'rgba(239, 68, 68, 0.2)' },
  'Bridge': { bg: '#a855f7', hover: '#9333ea', light: 'rgba(168, 85, 247, 0.2)' },
  'Outro': { bg: '#6b7280', hover: '#4b5563', light: 'rgba(107, 114, 128, 0.2)' },
  'Unknown': { bg: '#94a3b8', hover: '#64748b', light: 'rgba(148, 163, 184, 0.2)' },
};

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

function generateWaveformBars(sectionDuration, totalBars = 60) {
  const bars = [];
  const barsForSection = Math.max(3, Math.round(totalBars * (sectionDuration / 100)));
  
  for (let i = 0; i < barsForSection; i++) {
    const progress = i / barsForSection;
    const wave = Math.sin(progress * Math.PI * 4) * 0.3;
    const noise = (Math.random() - 0.5) * 0.4;
    const envelope = Math.sin(progress * Math.PI) * 0.3;
    const height = 0.3 + Math.abs(wave + noise + envelope) * 0.7;
    bars.push(Math.min(1, Math.max(0.15, height)));
  }
  return bars;
}

export default function SectionWaveform({ sections, totalDuration }) {
  const [hoveredSection, setHoveredSection] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const sectionData = useMemo(() => {
    if (!sections || sections.length === 0) return [];
    
    return sections.map((section, index) => {
      const widthPercent = (section.duration / totalDuration) * 100;
      const waveformBars = generateWaveformBars(widthPercent);
      const colors = sectionColors[section.type] || sectionColors['Unknown'];
      
      return {
        ...section,
        index,
        widthPercent,
        waveformBars,
        colors,
      };
    });
  }, [sections, totalDuration]);

  const handleMouseMove = (e, section) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setTooltipPosition({ x, y: y - 10 });
    }
    setHoveredSection(section);
  };

  const handleMouseLeave = () => {
    setHoveredSection(null);
  };

  if (!sections || sections.length === 0) {
    return (
      <div className="h-24 bg-muted/30 rounded-lg flex items-center justify-center text-sm text-muted-foreground">
        No section data available
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex h-24 rounded-lg overflow-hidden bg-gradient-to-b from-muted/20 to-muted/40 border border-muted/50">
        {sectionData.map((section) => (
          <div
            key={section.index}
            className="relative flex items-end justify-center gap-px cursor-pointer transition-all duration-200"
            style={{ 
              width: `${section.widthPercent}%`,
              backgroundColor: hoveredSection?.index === section.index ? section.colors.light : 'transparent',
            }}
            onMouseMove={(e) => handleMouseMove(e, section)}
            onMouseLeave={handleMouseLeave}
          >
            {section.waveformBars.map((height, barIndex) => (
              <div
                key={barIndex}
                className="transition-all duration-150"
                style={{
                  width: '2px',
                  height: `${height * 85}%`,
                  backgroundColor: hoveredSection?.index === section.index 
                    ? section.colors.hover 
                    : section.colors.bg,
                  opacity: hoveredSection?.index === section.index ? 1 : 0.85,
                  borderRadius: '1px',
                  marginBottom: '4px',
                }}
              />
            ))}
            
            {section.widthPercent > 10 && (
              <div 
                className="absolute bottom-0 left-0 right-0 text-center text-xs font-medium py-1 transition-opacity"
                style={{ 
                  color: section.colors.bg,
                  opacity: hoveredSection?.index === section.index ? 1 : 0.7,
                }}
              >
                {section.type}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-1 text-xs text-muted-foreground px-1">
        <span>0:00</span>
        <span>{formatTime(totalDuration)}</span>
      </div>

      {hoveredSection && (
        <div
          className="absolute z-50 pointer-events-none"
          style={{
            left: Math.min(tooltipPosition.x, containerRef.current?.offsetWidth - 220 || tooltipPosition.x),
            top: tooltipPosition.y - 160,
            transform: 'translateX(-50%)',
          }}
        >
          <div 
            className="bg-popover border border-border rounded-lg shadow-xl p-3 min-w-[200px]"
            style={{ 
              borderTop: `3px solid ${hoveredSection.colors.bg}`,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: hoveredSection.colors.bg }}
              />
              <span className="font-semibold text-sm">{hoveredSection.type}</span>
              {hoveredSection.energy_level && (
                <span 
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    hoveredSection.energy_level === 'High' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                    hoveredSection.energy_level === 'Medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  }`}
                >
                  <Zap className="h-3 w-3 inline mr-0.5" />
                  {hoveredSection.energy_level}
                </span>
              )}
            </div>
            
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{formatTime(hoveredSection.start_time)} - {formatTime(hoveredSection.end_time)}</span>
                <span className="text-foreground font-medium">({hoveredSection.bars} bars)</span>
              </div>
              
              {(hoveredSection.percussive_ratio !== undefined || hoveredSection.harmonic_ratio !== undefined) && (
                <div className="flex gap-3 pt-1">
                  {hoveredSection.percussive_ratio !== undefined && (
                    <div className="flex items-center gap-1">
                      <Drum className="h-3 w-3 text-orange-500" />
                      <span className="text-muted-foreground">{hoveredSection.percussive_ratio}%</span>
                    </div>
                  )}
                  {hoveredSection.harmonic_ratio !== undefined && (
                    <div className="flex items-center gap-1">
                      <Music className="h-3 w-3 text-blue-500" />
                      <span className="text-muted-foreground">{hoveredSection.harmonic_ratio}%</span>
                    </div>
                  )}
                </div>
              )}
              
              {hoveredSection.description && (
                <p className="text-muted-foreground pt-1 border-t border-muted/50 mt-1.5 leading-relaxed">
                  {hoveredSection.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
